/**
 * LogAIChatPanel - AI Chat interface for log analysis
 *
 * Provides conversational interface for the Log Intelligence Agent.
 * Integrates with the existing log viewer for bidirectional communication.
 *
 * @see Phase 2 Deliverable 2.6
 */

import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Send, ThumbsUp, ThumbsDown, Loader2, Sparkles, AlertCircle, Clock, Cpu, Cloud, Server, Search, Bell, Code, GitBranch, Clipboard, Check, ChevronDown, ChevronRight, Zap, Eye } from 'lucide-react';
import type { LogEvent } from '../../../utils/WeBelieveLogger.types';

/**
 * Chat message structure
 */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: {
    intent?: string;
    modelUsed?: 'mistral' | 'gpt-4' | 'claude';
    relatedLogs?: string[];
    patterns?: any[];
    processingTimeMs?: number;
    complexityScore?: number;
  };
}

/**
 * API response from log-intelligence endpoint
 */
interface AnalysisResponse {
  intent: string;
  analysis: {
    summary: string;
    rootCause?: string;
    recommendations: string[];
    relatedLogs: string[];
    confidence: number;
    processingTimeMs: number;
  };
  patterns: any[];
  logs: Array<{ 
    id: number | string;
    timestamp: string;
    session_id: string;
    component: string;
  }>;
  metadata: {
    modelUsed: 'mistral' | 'gpt-4' | 'claude';
    complexityScore: number;
    processingTimeMs: number;
  };
}

interface LogAIChatPanelProps {
  realmId: string;
  onHighlightLogs: (logIds: string[]) => void;
  forceModel?: 'auto' | 'local' | 'cloud';
  className?: string;
}

/**
 * Handle exposed via ref for external control
 */
export interface LogAIChatPanelHandle {
  /** Inject an explanation request for a specific log with context */
  explainLog: (focusLog: LogEvent, contextLogs: LogEvent[]) => Promise<void>;
}

/**
 * Extended API response for log explanation
 */
interface ExplainLogResponse {
  intent: string;
  analysis: {
    summary: string;
    rootCause?: string;
    recommendations: string[];
    relatedLogs: string[];
    confidence: number;
    processingTimeMs: number;
    // New fields for enhanced features
    suggestedFix?: {
      code: string;
      language: string;
      description: string;
    };
    rootCauseChain?: Array<{
      timestamp: string;
      component: string;
      message: string;
      isRoot: boolean;
    }>;
    codeContext?: {
      file: string;
      line: number;
      snippet: string;
      language: string;
    };
  };
  patterns: any[];
  logs: Array<{
    id: number | string;
    timestamp: string;
    session_id: string;
    component: string;
  }>;
  metadata: {
    modelUsed: 'mistral' | 'gpt-4' | 'claude';
    complexityScore: number;
    processingTimeMs: number;
  };
}

/**
 * Get component hints based on component name for better AI context
 */
function getComponentHints(component: string): {
  category: string;
  layer: string;
  relatedComponents: string[];
  commonIssues: string[];
} {
  const componentLower = component.toLowerCase();

  // Determine category and layer based on component naming conventions
  if (componentLower.includes('api') || componentLower.includes('service') || componentLower.includes('client')) {
    return {
      category: 'API/Service Layer',
      layer: 'backend',
      relatedComponents: ['HTTP Client', 'Request Handler', 'Response Parser'],
      commonIssues: ['Network timeouts', 'Authentication failures', 'Rate limiting', 'Invalid responses'],
    };
  }

  if (componentLower.includes('auth') || componentLower.includes('login') || componentLower.includes('session')) {
    return {
      category: 'Authentication',
      layer: 'cross-cutting',
      relatedComponents: ['JWT Handler', 'OAuth Provider', 'Session Store'],
      commonIssues: ['Token expiration', 'Invalid credentials', 'Session timeout', 'Permission denied'],
    };
  }

  if (componentLower.includes('db') || componentLower.includes('database') || componentLower.includes('query') || componentLower.includes('repository')) {
    return {
      category: 'Data Layer',
      layer: 'backend',
      relatedComponents: ['TypeORM', 'Query Builder', 'Connection Pool'],
      commonIssues: ['Connection pool exhausted', 'Query timeout', 'Deadlock', 'Constraint violation'],
    };
  }

  if (componentLower.includes('ui') || componentLower.includes('component') || componentLower.includes('page') || componentLower.includes('view')) {
    return {
      category: 'UI Component',
      layer: 'frontend',
      relatedComponents: ['React Component', 'State Manager', 'Event Handler'],
      commonIssues: ['Render errors', 'State sync issues', 'Event handler failures', 'Memory leaks'],
    };
  }

  if (componentLower.includes('router') || componentLower.includes('navigation') || componentLower.includes('route')) {
    return {
      category: 'Navigation',
      layer: 'frontend',
      relatedComponents: ['React Router', 'History', 'Location'],
      commonIssues: ['Invalid route', 'Navigation blocked', 'Route guard failure'],
    };
  }

  if (componentLower.includes('log') || componentLower.includes('telemetry') || componentLower.includes('metric')) {
    return {
      category: 'Observability',
      layer: 'infrastructure',
      relatedComponents: ['Logger', 'Metrics Collector', 'Trace Exporter'],
      commonIssues: ['Buffer overflow', 'Transport failure', 'Serialization error'],
    };
  }

  if (componentLower.includes('worker') || componentLower.includes('job') || componentLower.includes('queue') || componentLower.includes('task')) {
    return {
      category: 'Background Processing',
      layer: 'backend',
      relatedComponents: ['Job Queue', 'Worker Pool', 'Scheduler'],
      commonIssues: ['Job timeout', 'Queue backlog', 'Worker crash', 'Retry exhausted'],
    };
  }

  // Default fallback
  return {
    category: 'General',
    layer: 'unknown',
    relatedComponents: [],
    commonIssues: ['General error', 'Unexpected state', 'Resource unavailable'],
  };
}

/**
 * Quick action button component
 */
const QuickAction: React.FC<{
  label: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ label, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300
               rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors
               disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {label}
  </button>
);

/**
 * Root Cause Chain Visualization (#6)
 * Shows the causation flow with timestamps
 */
const RootCauseChain: React.FC<{
  chain: Array<{
    timestamp: string;
    component: string;
    message: string;
    isRoot: boolean;
  }>;
}> = ({ chain }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!chain || chain.length === 0) return null;

  return (
    <div className="mt-3 border border-orange-200 dark:border-orange-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 text-sm font-medium"
      >
        <GitBranch className="w-4 h-4" />
        Root Cause Chain
        {isExpanded ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
      </button>
      {isExpanded && (
        <div className="p-3 bg-white dark:bg-gray-800 space-y-2">
          {chain.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2">
              {/* Timeline connector */}
              <div className="flex flex-col items-center">
                <div className={`w-3 h-3 rounded-full ${item.isRoot ? 'bg-red-500' : 'bg-gray-400'}`} />
                {idx < chain.length - 1 && <div className="w-0.5 h-6 bg-gray-300 dark:bg-gray-600" />}
              </div>
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-mono">{new Date(item.timestamp).toLocaleTimeString()}</span>
                  <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">{item.component}</span>
                  {item.isRoot && <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded text-xs font-medium">ROOT</span>}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5 truncate">{item.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Code Snippet Preview (#4)
 * Shows actual source code from decoded source maps
 */
const CodeSnippetPreview: React.FC<{
  codeContext?: {
    file: string;
    line: number;
    snippet: string;
    language: string;
  };
}> = ({ codeContext }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!codeContext) return null;

  return (
    <div className="mt-3 border border-lex-accent rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-lex-accent-soft text-lex-accent text-sm font-medium"
      >
        <Code className="w-4 h-4" />
        Source Code
        <span className="ml-2 text-xs font-normal opacity-75">{codeContext.file}:{codeContext.line}</span>
        {isExpanded ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
      </button>
      {isExpanded && (
        <div className="bg-gray-900 text-gray-100 p-3 overflow-x-auto">
          <pre className="text-xs font-mono">
            <code>{codeContext.snippet}</code>
          </pre>
        </div>
      )}
    </div>
  );
};

/**
 * Suggested Fix with Copy Button (#9)
 */
const SuggestedFix: React.FC<{
  suggestedFix?: {
    code: string;
    language: string;
    description: string;
  };
}> = ({ suggestedFix }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  if (!suggestedFix) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(suggestedFix.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-3 border border-green-200 dark:border-green-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm font-medium"
      >
        <Zap className="w-4 h-4" />
        Suggested Fix
        {isExpanded ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
      </button>
      {isExpanded && (
        <div className="bg-white dark:bg-gray-800">
          <p className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            {suggestedFix.description}
          </p>
          <div className="relative">
            <button
              onClick={handleCopy}
              className="absolute top-2 right-2 p-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
              title="Copy to clipboard"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Clipboard className="w-4 h-4 text-gray-500" />}
            </button>
            <div className="bg-gray-900 text-gray-100 p-3 overflow-x-auto">
              <pre className="text-xs font-mono">
                <code>{suggestedFix.code}</code>
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Investigate Mode Timeline (#2)
 * Visual timeline of context logs around the focus log
 */
const InvestigateTimeline: React.FC<{
  focusLog: LogEvent;
  contextLogs: LogEvent[];
  onLogClick?: (log: LogEvent) => void;
}> = ({ focusLog, contextLogs, onLogClick }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!contextLogs || contextLogs.length === 0) return null;

  const focusTime = new Date(focusLog.timestamp).getTime();

  return (
    <div className="mt-3 border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 text-sm font-medium"
      >
        <Eye className="w-4 h-4" />
        Timeline Context
        <span className="ml-2 text-xs font-normal opacity-75">{contextLogs.length} logs</span>
        {isExpanded ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
      </button>
      {isExpanded && (
        <div className="p-3 bg-white dark:bg-gray-800 max-h-60 overflow-y-auto">
          {contextLogs.map((log, idx) => {
            const isFocus = log.timestamp === focusLog.timestamp && log.message === focusLog.message;
            const timeDiff = new Date(log.timestamp).getTime() - focusTime;
            const timeDiffStr = timeDiff === 0 ? '±0ms' : timeDiff > 0 ? `+${timeDiff}ms` : `${timeDiff}ms`;

            return (
              <div
                key={idx}
                onClick={() => onLogClick?.(log)}
                className={`flex items-start gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors ${
                  isFocus
                    ? 'bg-purple-100 dark:bg-purple-900/50 ring-1 ring-purple-400'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {/* Level indicator */}
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                  log.level === 'error' ? 'bg-red-500' :
                  log.level === 'warn' ? 'bg-yellow-500' :
                  log.level === 'info' ? 'bg-blue-500' :
                  log.level === 'performance' ? 'bg-green-500' : 'bg-gray-400'
                }`} />
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-gray-500 dark:text-gray-400">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                    <span className={`font-mono ${timeDiff === 0 ? 'text-purple-600 dark:text-purple-400 font-medium' : 'text-gray-400'}`}>
                      {timeDiffStr}
                    </span>
                    <span className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">{log.component}</span>
                  </div>
                  <p className={`text-sm mt-0.5 truncate ${isFocus ? 'text-purple-800 dark:text-purple-200 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                    {log.message}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Watch This Pattern Button (#3)
 * Supports both watching and unwatching patterns
 */
const WatchPatternButton: React.FC<{
  pattern: string;
  component: string;
  onWatch: (pattern: string, component: string) => Promise<{ patternId?: number; alreadyExists?: boolean }>;
  onUnwatch: (patternId: number) => Promise<boolean>;
}> = ({ pattern, component, onWatch, onUnwatch }) => {
  const [status, setStatus] = useState<'idle' | 'watching' | 'unwatching'>('idle');
  const [patternId, setPatternId] = useState<number | null>(null);
  const [isWatched, setIsWatched] = useState(false);

  const handleWatch = async () => {
    setStatus('watching');
    try {
      const result = await onWatch(pattern, component);
      if (result.patternId) {
        setPatternId(result.patternId);
        setIsWatched(true);
      } else if (result.alreadyExists) {
        // Pattern already exists - treat as watched
        setIsWatched(true);
      }
    } finally {
      setStatus('idle');
    }
  };

  const handleUnwatch = async () => {
    if (!patternId) return;
    setStatus('unwatching');
    try {
      const success = await onUnwatch(patternId);
      if (success) {
        setIsWatched(false);
        setPatternId(null);
      }
    } finally {
      setStatus('idle');
    }
  };

  const isProcessing = status !== 'idle';

  if (isWatched) {
    return (
      <button
        onClick={handleUnwatch}
        disabled={isProcessing}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full transition-colors bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 hover:bg-red-100 dark:hover:bg-red-900/50 hover:text-red-700 dark:hover:text-red-300"
        title="Click to stop watching this pattern"
      >
        <Bell className={`w-3.5 h-3.5 ${isProcessing ? 'animate-spin' : ''}`} />
        {isProcessing ? 'Removing...' : 'Watching (Click to Stop)'}
      </button>
    );
  }

  return (
    <button
      onClick={handleWatch}
      disabled={isProcessing}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full transition-colors bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50"
    >
      <Bell className={`w-3.5 h-3.5 ${isProcessing ? 'animate-spin' : ''}`} />
      {isProcessing ? 'Setting Alert...' : 'Watch This Pattern'}
    </button>
  );
};

/**
 * Copy Response Button - Exports full AI analysis context as JSON
 */
const CopyResponseButton: React.FC<{
  message: EnhancedChatMessage;
}> = ({ message }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    // Build comprehensive JSON export
    const exportData = {
      timestamp: message.timestamp.toISOString(),
      aiResponse: {
        summary: message.content,
        metadata: message.metadata,
      },
      focusLog: message.focusLog ? {
        timestamp: message.focusLog.timestamp,
        level: message.focusLog.level,
        component: message.focusLog.component,
        message: message.focusLog.message,
        source: message.focusLog.source,
        data: message.focusLog.data,
        duration_ms: message.focusLog.duration_ms,
        session_id: message.focusLog.session_id,
      } : null,
      contextLogs: message.contextLogs?.map(log => ({
        timestamp: log.timestamp,
        level: log.level,
        component: log.component,
        message: log.message,
        duration_ms: log.duration_ms,
      })) || [],
      rootCauseChain: message.rootCauseChain || null,
      codeContext: message.codeContext || null,
      suggestedFix: message.suggestedFix || null,
    };

    navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full transition-colors ${
        copied
          ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
      }`}
      title="Copy full AI response, context logs, and original log as JSON"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Clipboard className="w-3.5 h-3.5" />}
      {copied ? 'Copied!' : 'Copy Response'}
    </button>
  );
};

/**
 * Chat message bubble component
 */
const ChatMessageBubble: React.FC<{
  message: ChatMessage;
  onFeedback: (messageId: string, rating: 1 | -1) => void;
}> = ({ message, onFeedback }) => {
  const isUser = message.role === 'user';
  const [feedbackGiven, setFeedbackGiven] = useState<1 | -1 | null>(null);

  const handleFeedback = (rating: 1 | -1) => {
    if (feedbackGiven !== null) return;
    setFeedbackGiven(rating);
    onFeedback(message.id, rating);
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`rounded-lg p-3 max-w-[85%] ${
          isUser
            ? 'bg-lex-accent text-lex-accent-contrast'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
        }`}
      >
        <div className="text-sm whitespace-pre-wrap">{message.content}</div>

        {!isUser && message.metadata && (
          <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600">
            {/* Metadata row */}
            <div className="flex items-center gap-2 text-xs opacity-75 mb-2">
              {message.metadata.modelUsed && (
                <span className="flex items-center gap-1">
                  <Cpu className="w-3 h-3" />
                  {message.metadata.modelUsed}
                </span>
              )}
              {message.metadata.relatedLogs && message.metadata.relatedLogs.length > 0 && (
                <span className="flex items-center gap-1">
                  •
                  <AlertCircle className="w-3 h-3" />
                  {message.metadata.relatedLogs.length} logs
                </span>
              )}
              {message.metadata.processingTimeMs && (
                <span className="flex items-center gap-1">
                  •
                  <Clock className="w-3 h-3" />
                  {message.metadata.processingTimeMs}ms
                </span>
              )}
            </div>

            {/* Feedback buttons */}
            <div className="flex items-center gap-2">
              <span className="text-xs opacity-60">Was this helpful?</span>
              <button
                onClick={() => handleFeedback(1)}
                disabled={feedbackGiven !== null}
                className={`p-1 rounded transition-colors ${
                  feedbackGiven === 1
                    ? 'bg-green-500 text-white'
                    : feedbackGiven !== null
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title="Helpful"
              >
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleFeedback(-1)}
                disabled={feedbackGiven !== null}
                className={`p-1 rounded transition-colors ${
                  feedbackGiven === -1
                    ? 'bg-red-500 text-white'
                    : feedbackGiven !== null
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title="Not helpful"
              >
                <ThumbsDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Extended chat message with enhanced features data
 */
interface EnhancedChatMessage extends ChatMessage {
  // Enhanced feature data
  focusLog?: LogEvent;
  contextLogs?: LogEvent[];
  rootCauseChain?: Array<{
    timestamp: string;
    component: string;
    message: string;
    isRoot: boolean;
  }>;
  codeContext?: {
    file: string;
    line: number;
    snippet: string;
    language: string;
  };
  suggestedFix?: {
    code: string;
    language: string;
    description: string;
  };
}

/**
 * Main LogAIChatPanel component
 */
export const LogAIChatPanel = forwardRef<LogAIChatPanelHandle, LogAIChatPanelProps>(({
  realmId,
  onHighlightLogs,
  forceModel = 'auto',
  className = '',
}, ref) => {
  const [messages, setMessages] = useState<EnhancedChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load messages from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`log-ai-chat-${realmId}`);
      if (saved) {
        const parsed = JSON.parse(saved) as ChatMessage[];
        // Restore dates
        setMessages(parsed.map(m => ({ ...m, timestamp: new Date(m.timestamp) })));
      }
    } catch (e) {
      console.warn('[LogAIChatPanel] Failed to load saved messages:', e);
    }
  }, [realmId]);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        // Only save last 20 messages
        const toSave = messages.slice(-20);
        localStorage.setItem(`log-ai-chat-${realmId}`, JSON.stringify(toSave));
      } catch (e) {
        console.warn('[LogAIChatPanel] Failed to save messages:', e);
      }
    }
  }, [messages, realmId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /**
   * Handle Watch Pattern alert - returns patternId on success
   */
  const handleWatchPattern = useCallback(async (pattern: string, component: string): Promise<{ patternId?: number; alreadyExists?: boolean }> => {
    try {
      const token = localStorage.getItem('webelieve_access_token');
      const response = await fetch('/api/v1/log-intelligence/watch-pattern', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          pattern,
          component,
          realmId,
        }),
      });

      if (response.status === 409) {
        // Pattern already exists
        const data = await response.json();
        return { patternId: data.patternId, alreadyExists: true };
      }

      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }

      const data = await response.json();
      return { patternId: data.patternId };
    } catch (err) {
      console.error('[LogAIChatPanel] Failed to create pattern watch:', err);
      return {};
    }
  }, [realmId]);

  /**
   * Handle Unwatch Pattern - removes a watched pattern
   */
  const handleUnwatchPattern = useCallback(async (patternId: number): Promise<boolean> => {
    try {
      const token = localStorage.getItem('webelieve_access_token');
      const response = await fetch(`/api/v1/log-intelligence/watch-pattern/${patternId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }

      return true;
    } catch (err) {
      console.error('[LogAIChatPanel] Failed to remove pattern watch:', err);
      return false;
    }
  }, []);

  /**
   * Explain a specific log with context - called externally via ref
   */
  const explainLog = useCallback(async (focusLog: LogEvent, contextLogs: LogEvent[]) => {
    if (loading) return;

    // Create a descriptive user message
    const userMessage: EnhancedChatMessage = {
      id: `user-explain-${Date.now()}`,
      role: 'user',
      content: `🔍 Explain this ${focusLog.level.toUpperCase()} log:\n\n**[${focusLog.component}]** ${focusLog.message}`,
      timestamp: new Date(),
      focusLog,
      contextLogs,
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('webelieve_access_token');

      // Prepare conversation history
      const conversationHistory = messages.slice(-6).map(m => ({
        role: m.role,
        content: m.content,
      }));

      // Build rich codebase context for better AI analysis
      const codebaseContext = {
        // Application stack
        stack: {
          frontend: {
            framework: 'React 18',
            language: 'TypeScript 5.x',
            styling: 'Tailwind CSS',
            stateManagement: 'React Context + Hooks',
            routing: 'React Router v6',
            buildTool: 'Vite',
            bundler: 'ESBuild',
          },
          backend: {
            runtime: 'Node.js',
            framework: 'NestJS',
            language: 'TypeScript',
            database: 'PostgreSQL',
            orm: 'TypeORM',
            auth: 'JWT + OAuth2',
          },
          infrastructure: {
            deployment: 'Docker / Kubernetes',
            logging: 'Custom centralized logging with PostgreSQL',
            monitoring: 'Custom observability platform',
          },
        },
        // App-specific context
        appInfo: {
          name: 'EdgeLex',
          type: 'Enterprise Legal Technology Platform',
          modules: ['Observability', 'Knowledge Graph', 'Document Processing', 'AI Analysis'],
        },
        // Component hierarchy hints based on component name
        componentHints: getComponentHints(focusLog.component),
        // Extract any version info from log data
        versionInfo: focusLog.data?.appVersion || focusLog.data?.version || null,
        // Browser/client info if available
        clientInfo: focusLog.data?.userAgent || focusLog.data?.browser || null,
        // Session context
        sessionInfo: {
          sessionId: focusLog.session_id,
          userId: focusLog.user_id,
          source: focusLog.source?.file || null,
        },
      };

      // Build stack trace summary if available
      const stackTraceSummary = focusLog.source?.stack
        ? {
            hasStack: true,
            file: focusLog.source.file,
            line: focusLog.source.line,
            function: focusLog.source.function,
            stackPreview: focusLog.source.stack
              .slice(0, 5)
              .map(frame => `${frame.file || 'unknown'}:${frame.line || 0} ${frame.function || 'anonymous'}`)
              .join('\n'),
          }
        : null;

      // Analyze context log patterns
      const contextAnalysis = {
        totalContextLogs: contextLogs.length,
        errorCount: contextLogs.filter(l => l.level === 'error').length,
        warnCount: contextLogs.filter(l => l.level === 'warn').length,
        uniqueComponents: [...new Set(contextLogs.map(l => l.component))],
        timeSpanMs: contextLogs.length > 1
          ? new Date(contextLogs[contextLogs.length - 1].timestamp).getTime() -
            new Date(contextLogs[0].timestamp).getTime()
          : 0,
        hasPerformanceIssues: contextLogs.some(l => l.duration_ms && l.duration_ms > 100),
      };

      // Call the API with focusLog, contextLogs, and rich codebase context
      const response = await fetch('/api/v1/log-intelligence/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: `Explain this ${focusLog.level} log from component "${focusLog.component}": "${focusLog.message}"`,
          realmId,
          conversationHistory,
          forceModel: forceModel !== 'auto' ? forceModel : undefined,
          // Rich pre-contextualization for better AI analysis
          codebaseContext,
          stackTraceSummary,
          contextAnalysis,
          // Enhanced payload for single-log explanation
          focusLog: {
            id: (focusLog as any).id, // DB ID for Weaviate embedding lookup
            timestamp: focusLog.timestamp,
            level: focusLog.level,
            component: focusLog.component,
            message: focusLog.message,
            source: focusLog.source,
            data: focusLog.data,
            duration_ms: focusLog.duration_ms,
            session_id: focusLog.session_id,
            user_id: focusLog.user_id,
          },
          contextLogs: contextLogs.map(l => ({
            timestamp: l.timestamp,
            level: l.level,
            component: l.component,
            message: l.message,
            duration_ms: l.duration_ms,
            data: l.data,
          })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed with status ${response.status}`);
      }

      const result: ExplainLogResponse = await response.json();

      // Create enhanced AI response with all the features
      const aiMessage: EnhancedChatMessage = {
        id: `ai-explain-${Date.now()}`,
        role: 'assistant',
        content: result.analysis.summary,
        timestamp: new Date(),
        metadata: {
          intent: result.intent,
          modelUsed: result.metadata.modelUsed,
          relatedLogs: result.logs.map(l => String(l.id)),
          patterns: result.patterns,
          processingTimeMs: result.metadata.processingTimeMs,
          complexityScore: result.metadata.complexityScore,
        },
        // Enhanced features data
        focusLog,
        contextLogs,
        rootCauseChain: result.analysis.rootCauseChain,
        codeContext: result.analysis.codeContext,
        suggestedFix: result.analysis.suggestedFix,
      };

      setMessages(prev => [...prev, aiMessage]);

      // Highlight related logs in main view
      if (result.logs.length > 0) {
        onHighlightLogs(result.logs.map(l => `${l.timestamp}-${l.session_id}-${l.component}`));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);

      const errorAiMessage: EnhancedChatMessage = {
        id: `ai-error-${Date.now()}`,
        role: 'assistant',
        content: `I encountered an error while analyzing this log: ${errorMessage}. Please try again.`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorAiMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [loading, messages, realmId, forceModel, onHighlightLogs]);

  // Expose explainLog method via ref
  useImperativeHandle(ref, () => ({
    explainLog,
  }), [explainLog]);

  /**
   * Send message to backend
   */
  const handleSend = useCallback(async () => {
    if (!input.trim() || loading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('webelieve_access_token');
      
      // Prepare conversation history (last 3 exchanges = 6 messages)
      const conversationHistory = messages.slice(-6).map(m => ({
        role: m.role,
        content: m.content,
      }));

      const response = await fetch('/api/v1/log-intelligence/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: userMessage.content,
          realmId,
          conversationHistory,
          forceModel: forceModel !== 'auto' ? forceModel : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed with status ${response.status}`);
      }

      const result: AnalysisResponse = await response.json();

      const aiMessage: ChatMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: result.analysis.summary,
        timestamp: new Date(),
        metadata: {
          intent: result.intent,
          modelUsed: result.metadata.modelUsed,
          relatedLogs: result.logs.map(l => String(l.id)),
          patterns: result.patterns,
          processingTimeMs: result.metadata.processingTimeMs,
          complexityScore: result.metadata.complexityScore,
        },
      };

      setMessages(prev => [...prev, aiMessage]);

      // Highlight related logs in main view using composite key (timestamp + session_id + component)
      if (result.logs.length > 0) {
        onHighlightLogs(result.logs.map(l => `${l.timestamp}-${l.session_id}-${l.component}`));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      
      // Add error message to chat
      const errorAiMessage: ChatMessage = {
        id: `ai-error-${Date.now()}`,
        role: 'assistant',
        content: `I encountered an error while analyzing your logs: ${errorMessage}. Please try again or rephrase your question.`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorAiMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, messages, realmId, onHighlightLogs]);

  /**
   * Handle Enter key press
   */
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  /**
   * Handle feedback
   */
  const handleFeedback = useCallback(async (messageId: string, rating: 1 | -1) => {
    try {
      const token = localStorage.getItem('webelieve_access_token');
      await fetch('/api/v1/log-intelligence/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          queryHash: messageId,
          rating: rating === 1 ? 'up' : 'down',
        }),
      });
    } catch (err) {
      console.warn('[LogAIChatPanel] Failed to submit feedback:', err);
    }
  }, [realmId]);

  /**
   * Quick action handlers
   */
  const handleQuickAction = (query: string) => {
    setInput(query);
    // Auto-send after a short delay
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  /**
   * Clear chat history
   */
  const handleClearHistory = () => {
    setMessages([]);
    localStorage.removeItem(`log-ai-chat-${realmId}`);
  };

  return (
    <div className={`flex flex-col h-full bg-white dark:bg-gray-800 ${className}`}>
      {/* Chat messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">Log Intelligence Assistant</p>
            <p className="text-sm mt-1">Ask me about errors, performance issues, or patterns in your logs.</p>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id}>
            <ChatMessageBubble
              message={message}
              onFeedback={handleFeedback}
            />
            {/* Enhanced features for AI explanation responses */}
            {message.role === 'assistant' && message.focusLog && (
              <div className="ml-0 mt-2 space-y-2">
                {/* Investigate Timeline (#2) */}
                {message.contextLogs && message.contextLogs.length > 0 && (
                  <InvestigateTimeline
                    focusLog={message.focusLog}
                    contextLogs={message.contextLogs}
                    onLogClick={(log) => {
                      const logKey = `${log.timestamp}-${log.session_id}-${log.component}`;
                      onHighlightLogs([logKey]);
                    }}
                  />
                )}

                {/* Root Cause Chain (#6) */}
                {message.rootCauseChain && message.rootCauseChain.length > 0 && (
                  <RootCauseChain chain={message.rootCauseChain} />
                )}

                {/* Code Snippet Preview (#4) */}
                {message.codeContext && (
                  <CodeSnippetPreview codeContext={message.codeContext} />
                )}

                {/* Suggested Fix (#9) */}
                {message.suggestedFix && (
                  <SuggestedFix suggestedFix={message.suggestedFix} />
                )}

                {/* Action Buttons Row */}
                <div className="flex items-center gap-2 mt-2">
                  {/* Copy Response Button - always visible for AI responses with context */}
                  <CopyResponseButton message={message} />

                  {/* Watch Pattern Button (#3) - only for error/warn */}
                  {message.focusLog && (message.focusLog.level === 'error' || message.focusLog.level === 'warn') && (
                    <WatchPatternButton
                      pattern={message.focusLog.message.substring(0, 50)}
                      component={message.focusLog.component}
                      onWatch={handleWatchPattern}
                      onUnwatch={handleUnwatchPattern}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="rounded-lg p-3 bg-gray-100 dark:bg-gray-700">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Analyzing logs...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
        {/* Error display */}
        {error && (
          <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Input row */}
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your logs..."
            disabled={loading}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       placeholder-gray-500 dark:placeholder-gray-400
                       focus:outline-none focus:ring-2 focus:ring-lex-accent focus:border-transparent
                       disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2 mt-3">
          <QuickAction
            label="Recent Errors"
            onClick={() => handleQuickAction('Show me recent errors')}
            disabled={loading}
          />
          <QuickAction
            label="Performance Issues"
            onClick={() => handleQuickAction('Explain performance issues in the last hour')}
            disabled={loading}
          />
          <QuickAction
            label="Component Health"
            onClick={() => handleQuickAction('Analyze component health')}
            disabled={loading}
          />
          <QuickAction
            label="Similar Errors"
            onClick={() => handleQuickAction('Find similar historical errors')}
            disabled={loading}
          />
        </div>

        {/* Clear history link */}
        {messages.length > 0 && (
          <div className="mt-3 text-center">
            <button
              onClick={handleClearHistory}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
            >
              Clear chat history
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

// Display name for debugging
LogAIChatPanel.displayName = 'LogAIChatPanel';

export default LogAIChatPanel;
